import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const CallCenterCentresFormation = () => {
  return (
    <>
      <Helmet>
        <title>Call Center pour Centres de Formation - ZiExter</title>
        <meta name="description" content="Externalisez votre accueil et gestion administrative avec ZiExter : prise de RDV, inscriptions, suivi formations, relances, plannings. Offrez à vos apprenants un accompagnement fluide et professionnel." />
        <link rel="canonical" href="https://ziexter.com/services/call-center-centres-de-formation" />
      </Helmet>

      {/* Hero */}
      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <nav className="flex items-center space-x-2 text-sm mb-4 text-gray-200">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>/</span>
              <span className="text-white">Call Center pour Centres de Formation</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              🎓 ZiExter – Call Center Spécial Centres de Formation
            </h1>
            <p className="text-xl text-gray-100 mb-4">
              Offrez à vos apprenants un accompagnement fluide et professionnel
            </p>
            <p className="text-lg text-gray-200">
              Gérer les appels, organiser les rendez-vous et assurer le suivi administratif des apprenants est un défi quotidien pour les centres de formation.
            </p>
            <p className="text-lg text-gray-200 mt-4">
              Avec ZiExter, libérez vos équipes pédagogiques de la charge administrative et concentrez-vous sur l'essentiel : la réussite de vos apprenants.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Hero */}
      <section className="py-8 bg-accent">
        <div className="container-custom">
          <div className="text-center">
            <p className="text-white text-lg mb-4">
              👉 Prenez rendez-vous avec nos experts et découvrez comment notre call center spécialisé transforme l'expérience de vos apprenants.
            </p>
            <Link to="/rendez-vous" className="btn btn-secondary">
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      </section>

      {/* Services dédiés */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">📞 Nos services dédiés aux centres de formation</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card hover-lift animate-fade-in-up">
              <h3 className="text-xl font-bold mb-4">✅ Gestion des appels</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Réception et traitement des appels entrants des apprenants
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Relances téléphoniques pour inscriptions et suivi administratif
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Orientation claire et personnalisée vers les bons interlocuteurs
                </li>
              </ul>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <h3 className="text-xl font-bold mb-4">📅 Prise de rendez-vous</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Planification des sessions de formation et ateliers
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Coordination entre apprenants, formateurs et salles
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Relances automatiques pour maximiser la présence aux sessions
                </li>
              </ul>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-200">
              <h3 className="text-xl font-bold mb-4">🖥️ Aide à la saisie et gestion des données</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Enregistrement rapide et fiable des inscriptions
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Mise à jour et suivi des dossiers apprenants
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Gestion des absences et reporting statistique
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi choisir ZiExter */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">🚀 Pourquoi choisir ZiExter ?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '⏱️', title: 'Gain de temps', desc: 'Vos formateurs se concentrent sur la pédagogie, nous gérons le reste.' },
              { icon: '✅', title: 'Qualité garantie', desc: 'Chaque interaction est suivie et mesurée.' },
              { icon: '🔄', title: 'Flexibilité totale', desc: 'Service ajustable selon vos périodes d\'inscription.' },
              { icon: '🔒', title: 'Conformité RGPD', desc: 'Protection maximale des données apprenants.' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift text-center animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="text-4xl mb-3">{item.icon}</div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Notre approche */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">🔎 Notre approche en 4 étapes</h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              { num: '1', title: 'Analyse de vos besoins', desc: 'Compréhension de votre organisation et de vos formations.' },
              { num: '2', title: 'Mise en place', desc: 'Scripts personnalisés, intégration avec vos outils.' },
              { num: '3', title: 'Exécution', desc: 'Gestion des appels, rendez-vous et saisie des données.' },
              { num: '4', title: 'Suivi & Reporting', desc: 'Rapports transparents et optimisations continues.' }
            ].map((item, i) => (
              <div key={i} className="card flex items-start hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center font-bold text-xl mr-4 flex-shrink-0">
                  {item.num}
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Avis clients */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">💬 Avis de nos clients</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="card hover-lift animate-fade-in-up">
              <p className="text-gray-700 mb-4 italic">
                "Depuis que nous travaillons avec ZiExter, nos inscriptions sont mieux suivies et nos apprenants reçoivent un accompagnement constant."
              </p>
              <div className="font-semibold">Sophie L.</div>
              <div className="text-sm text-gray-600">Directrice pédagogique – Centre EDUCASER</div>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <p className="text-gray-700 mb-4 italic">
                "Les relances sont faites à temps et nos taux de présence ont nettement augmenté."
              </p>
              <div className="font-semibold">Karim D.</div>
              <div className="text-sm text-gray-600">Responsable formation – EduPro France</div>
            </div>
          </div>
        </div>
      </section>

      {/* Chiffres clés */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">📊 Nos chiffres clés en support formation</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { stat: '95%', label: 'de taux de satisfaction des apprenants' },
              { stat: '20 sec', label: 'de temps moyen de réponse aux appels' },
              { stat: '+30%', label: 'd\'amélioration du taux de présence aux sessions' },
              { stat: '24/7', label: 'disponibilité selon vos besoins' }
            ].map((item, i) => (
              <div key={i} className="card text-center hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="text-3xl font-bold text-primary mb-2">{item.stat}</div>
                <p className="text-sm text-gray-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">❓ FAQ – Call Center Centres de Formation</h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>➡ Quels types de formations couvrez-vous ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Toutes : continues, professionnelles, présentielles ou en ligne.
              </div>
            </details>

            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>➡ Pouvez-vous gérer uniquement les appels sortants ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Oui, nous adaptons nos services selon vos besoins (entrants, sortants ou mixtes).
              </div>
            </details>

            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>➡ Comment assurez-vous la confidentialité des données ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Toutes nos opérations respectent le RGPD et des protocoles stricts de sécurité.
              </div>
            </details>

            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>➡ Aurons-nous un suivi en temps réel ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Oui, un tableau de bord transparent est inclus pour suivre appels, rendez-vous et saisie.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              ✨ Passez à un accompagnement professionnel
            </h2>
            <p className="text-xl text-gray-100 mb-8">
              Ne laissez plus vos équipes être submergées par les tâches administratives.
              Avec ZiExter, vos apprenants bénéficient d'un call center spécialisé, fiable et disponible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn btn-accent btn-lg">
                Contactez-nous dès aujourd'hui
              </Link>
              <Link to="/devis" className="btn btn-outline-white btn-lg">
                Demander un devis
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CallCenterCentresFormation;
